import { useEffect, useRef, useState } from "react";
import { Plus, Copy, Upload } from "lucide-react";
import ImageUploader from "./ProductComponents/ImageUploader";
import { ChevronDown } from "lucide-react";
import { FormField } from "../Common/FormField";
import SearchableSelect from "../Common/SearchableSelect";
import { PRODUCT_DETAIL_FIELDS, SIZE_LIST } from "../Common/DataList";
import SizeTable from "./ProductComponents/SizeTable";
import { useParams } from "react-router-dom";
import {
  onCatalogProductClone,
  onCatalogProductCloneList,
  onCreateCatalogDelete,
  onCreateCatalogGet,
  onCreateCatalogProduct,
} from "../../../redux/homeAction";
import { useDispatch, useSelector } from "react-redux";
import BottomHeader from "../Common/BottomHeader";
import TopTapProductAdd from "../Common/TopTapProductAdd";
import TopHeader from "../Common/TopHeader";
import FullScreenLoader from "../../Common/FullScreenLoader";
import {
  setProducts,
  setActiveIndex,
  updateProductField,
  applySizes,
  copyProduct,
  addProduct,
  setCopyAll,
  deleteProduct,
} from "../../../redux/slices/productSlice";

export default function ProductStep2() {
  const fileRef = useRef(null);
  const { catalogId } = useParams();
  const dispatch = useDispatch();
  const { products, activeIndex, copyAll } = useSelector(
    (state) => state.product,
  );
  const [selectedPreviewImage, setSelectedPreviewImage] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const dropdownRef = useRef(null);

  const current = products?.[activeIndex];

  const getCreateCatalogGet = useSelector(
    (state) => state.home.getCreateCatalogGet,
  );
  const getCreateCatalogDelete = useSelector(
    (state) => state.home.getCreateCatalogDelete,
  );
  const getCatalogProductClone = useSelector(
    (state) => state.home.getCatalogProductClone,
  );
  const getCatalogProductCloneList = useSelector(
    (state) => state.home.getCatalogProductCloneList,
  );
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const [sizeOpen, setSizeOpen] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState([
    "XS",
    "S",
    "M",
    "L",
    "XL",
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  // =========================
  // LOAD DATA
  // =========================
  useEffect(() => {
    const saved = localStorage.getItem(`catalog-products-${catalogId}`);

    if (saved) {
      dispatch(setProducts(JSON.parse(saved)));
      setIsInitialized(true);
      return;
    }

    if (!getCreateCatalogGet?.draft) return;

    const mappedProducts = getCreateCatalogGet.draft.products.map((p) => ({
      ...p,
      id: p._id,

      // ✅ DIRECT ARRAY (NO CONVERSION)
      images: Array.isArray(p.images) ? p.images : [],

      selectedSizes: Array.isArray(p.selectedSizes) ? p.selectedSizes : [],
      sizes: Array.isArray(p.sizes) ? p.sizes : [],
    }));

    dispatch(setProducts(mappedProducts));
    setIsInitialized(true);
  }, [getCreateCatalogGet, catalogId, dispatch]);

  useEffect(() => {
    dispatch(onCreateCatalogGet(catalogId, setLoading));
  }, [catalogId, dispatch]);

  useEffect(() => {
    dispatch(onCatalogProductCloneList());
  }, []);
  // =========================
  // LOCAL STORAGE SAVE
  // =========================
  useEffect(() => {
    if (!isInitialized) return;
    const timer = setTimeout(() => {
      localStorage.setItem(
        `catalog-products-${catalogId}`,
        JSON.stringify(products),
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [products, catalogId, isInitialized]);

  // =========================
  // UPDATE FIELD (REDUX)
  // =========================
  const update = (key, value) => {
    dispatch(
      updateProductField({
        index: activeIndex,
        key,
        value,
      }),
    );
  };

  // =========================
  // APPLY SIZES (REDUX)
  // =========================
  const handleApplySizes = () => {
    dispatch(
      applySizes({
        index: activeIndex,
        sizes: selectedSizes,
      }),
    );
    setSizeOpen(false);
  };

  // =========================
  // COPY PRODUCT
  // =========================
  const handleCopy = (index) => {
    dispatch(copyProduct(index));
  };

  const normalizeImages = (images) => {
    if (!images) return [];

    if (Array.isArray(images)) return images;

    return [
      { type: "front", url: images.front || "" },
      { type: "side", url: images.side || "" },
      { type: "back", url: images.back || "" },
      { type: "zoom", url: images.zoom || "" },
    ];
  };
  // =========================
  // ADD PRODUCT IMAGE (API)
  // =========================
  const handleFirstImage = async (file) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("catalogId", catalogId);
      formData.append("images", file);

      const res = await dispatch(
        onCreateCatalogProduct(catalogId, formData, setLoading),
      );

      const newProduct = res.data.product;

      const base = products[0] || {};

      const newEntry = {
        ...structuredClone(base),
        id: newProduct._id,
        _id: newProduct._id,

        images: normalizeImages(newProduct.images),
      };

      dispatch(addProduct(newEntry));
    } catch (err) {}
  };

  // =========================
  // DELETE PRODUCT
  // =========================
  const handleDelete = async () => {
    if (products.length === 1) {
      alert("At least one product required");
      return;
    }

    setLoading(true);

    const productId = products[activeIndex].id;

    const updated = products.filter((_, i) => i !== activeIndex);

    dispatch(onCreateCatalogDelete(catalogId, productId, setLoading));
    dispatch(deleteProduct(activeIndex));
  };

  const handleSelectCatalogToClone = async (selectedCatalogId) => {
    try {
      setLoading(true);

      const response = await dispatch(
        onCatalogProductClone(selectedCatalogId, setLoading),
      );

      const sourceCatalog = response?.data?.catalog;

      if (sourceCatalog && sourceCatalog.products) {
        // Map source products but KEEP the current product's existing images & ID
        const autofilledProducts = sourceCatalog.products.map((p, index) => {
          // Agar current state mein us index par koi product aur uski images pehle se hain, toh unhe retain karein
          const existingProduct = products[index];

          return {
            ...p,
            id: existingProduct?.id || p._id || crypto.randomUUID(),
            _id: existingProduct?._id || p._id,
            // 🖼️ Yeh ensure karega ki agar pehle se images uploaded hain, toh wo override na ho
            images:
              existingProduct?.images?.length > 0
                ? existingProduct.images
                : Array.isArray(p.images)
                  ? p.images
                  : [],
            name: p.name || "",
          };
        });

        // Redux me products set kar do
        dispatch(setProducts(autofilledProducts));
        dispatch(setActiveIndex(0));

        alert(
          "Catalog data successfully auto-filled! Aapki existing images safe hain.",
        );
      }
    } catch (error) {
      console.error("Error autofilling catalog:", error);
    } finally {
      setLoading(false);
    }
  };
  // =========================
  // ADD PRODUCT CLICK
  // =========================
  const handleAddProduct = () => {
    fileRef.current?.click();
  };

  useEffect(() => {
    setSelectedSizes(current?.selectedSizes || []);
  }, [activeIndex]);

  return (
    <div className="min-h-screen bg-gray-100 p-3 md:p-4">
      <div
        className="bg-indigo-50 border border-indigo-200 p-4 rounded-xl mb-4 relative"
        ref={dropdownRef}
      >
        <label className="block text-sm font-semibold text-indigo-900 mb-2">
          🔄 Purane Catalog se Data Auto-Fill karein (Quick Clone):
        </label>

        {/* Custom Dropdown Trigger Box */}
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="w-full md:w-1/2 h-12 border border-indigo-300 rounded-lg px-3 bg-white flex items-center justify-between cursor-pointer shadow-sm hover:border-indigo-400"
        >
          {selectedCatalog ? (
            <div className="flex items-center gap-3 overflow-hidden">
              <img
                src={selectedCatalog.previewImage}
                alt=""
                className="w-8 h-8 object-cover rounded-md border flex-shrink-0"
              />
              <span className="text-sm text-gray-800 truncate">
                {selectedCatalog.sampleProductName} (
                {selectedCatalog.categoryName})
              </span>
            </div>
          ) : (
            <span className="text-sm text-gray-400">
              -- Select Existing Catalog to Copy Data --
            </span>
          )}
          <ChevronDown
            size={18}
            className={`text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </div>

        {/* Custom Dropdown Options List with Images */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full md:w-1/2 bg-white border border-indigo-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
            {getCatalogProductCloneList?.catalogs?.map((cat) => (
              <div
                key={cat.catalogId}
                onClick={() => {
                  setSelectedCatalog(cat);
                  setIsOpen(false);
                  handleSelectCatalogToClone(cat.catalogId); // Aapka auto-fill function
                }}
                className="flex items-center gap-3 p-3 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-none transition-colors"
              >
                <img
                  src={cat.previewImage}
                  alt=""
                  className="w-10 h-10 object-cover rounded-md border flex-shrink-0"
                />
                <div className="overflow-hidden">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {cat.sampleProductName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {cat.categoryName} • {cat.productCount} Products
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-indigo-600 mt-2">
          Catalog select karte hi saari common details automatically fill ho
          jayengi.
        </p>
      </div>
      <TopHeader select={2} />
      {/* HIDDEN INPUT */}
      <input
        type="file"
        hidden
        ref={fileRef}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) handleFirstImage(file); // 🔥 now API call
        }}
      />

      {/* TOP PRODUCT TABS */}
      <TopTapProductAdd
        products={products}
        activeIndex={activeIndex}
        setActiveIndex={(i) => dispatch(setActiveIndex(i))}
        handleAddProduct={handleAddProduct}
        handleCopy={handleCopy}
      />

      {/* ADD PRODUCT DETAILS HEADER */}
      <div className="bg-white rounded-xl p-3 md:p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h1 className="text-lg md:text-xl font-semibold">
            Add Product Details
          </h1>

          {products.length > 1 && (
            <button
              className="flex items-center gap-2 text-sm md:text-base text-indigo-600 font-medium hover:text-indigo-700"
              onClick={handleDelete}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7L5 7M10 11V17M14 11V17M6 7L7 19C7.1 20.1 8 21 9.1 21H14.9C16 21 16.9 20.1 17 19L18 7M9 7V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V7"
                />
              </svg>
              Delete this product
            </button>
          )}
        </div>

        <div className="mt-3 bg-gray-50 p-3 rounded-lg border">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={copyAll}
              onChange={(e) => dispatch(setCopyAll(e.target.checked))}
              className="mt-1 h-4 w-4 accent-indigo-600"
            />

            <div>
              <p className="text-sm font-medium">
                Copy input details to all products
              </p>

              <p className="text-xs text-gray-500">
                If you want to change specific fields for a particular product
                like Color, Fabric, etc., you can change them by selecting that
                product.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-12 gap-4">
        {/* LEFT FORM */}
        <div className="col-span-12 md:col-span-8 bg-white p-3 md:p-4 rounded-xl">
          {/* SECTION TITLE */}
          <h2 className="text-base md:text-lg font-semibold mb-3">
            Product, Size and Inventory
          </h2>

          {/* TOP FIELDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Net Weight (gms)" required>
              <input
                type="text"
                value={current?.weight}
                onChange={(e) => update("weight", e.target.value)}
                className="h-10 w-full border rounded px-3 text-sm"
              />
            </FormField>

            <FormField label="Style code/Product ID">
              <input
                placeholder="Enter Style code/ Product ID"
                value={current?.styleCode}
                onChange={(e) => update("styleCode", e.target.value)}
                className="h-10 w-full border rounded px-3 text-sm"
              />
            </FormField>

            <FormField label="Product Name" required>
              <input
                placeholder="Enter Product Name"
                value={current?.name}
                onChange={(e) => update("name", e.target.value)}
                className="h-10 w-full border rounded px-3 text-sm"
              />
            </FormField>

            {/* SIZE SELECT */}
            <FormField label="Size" required>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setSizeOpen(!sizeOpen)}
                  className="w-full h-12 border border-gray-300 rounded-lg px-4 text-left flex items-center justify-between bg-white"
                >
                  <span className="text-sm text-gray-700">
                    {current?.selectedSizes?.length
                      ? current.selectedSizes.join(", ")
                      : "Select Size"}
                  </span>

                  <ChevronDown
                    size={20}
                    className={`transition ${sizeOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {sizeOpen && (
                  <div className="absolute top-full left-0 mt-3 w-full sm:min-w-[350px] rounded-xl border border-gray-200 bg-white p-4 shadow-lg z-50">
                    <div className="grid grid-cols-3 gap-3">
                      {SIZE_LIST.map((size) => (
                        <label
                          key={size}
                          className="flex items-center gap-2 cursor-pointer text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSizes.includes(size)}
                            onChange={() => {
                              setSelectedSizes((prev) =>
                                prev.includes(size)
                                  ? prev.filter((s) => s !== size)
                                  : [...prev, size],
                              );
                            }}
                            className="h-4 w-4 accent-indigo-600"
                          />
                          <span>{size}</span>
                        </label>
                      ))}
                    </div>

                    <div className="mt-4 border-t pt-3 flex items-center justify-between">
                      <button
                        onClick={() => setSelectedSizes([])}
                        className="text-sm text-gray-500"
                      >
                        Clear All
                      </button>

                      <button
                        // onClick={() => {
                        //   setProducts((prev) =>
                        //     prev.map((p, i) =>
                        //       i === activeIndex
                        //         ? { ...p, sizes: selectedSizes }
                        //         : p,
                        //     ),
                        //   );
                        //   setSizeOpen(false);
                        // }}
                        onClick={handleApplySizes}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </FormField>
          </div>

          {/* SIZE TABLE */}
          {current?.selectedSizes?.length > 0 && (
            <SizeTable activeIndex={activeIndex} />
          )}

          {/* PRODUCT DETAILS */}
          <div className="mt-4 p-3">
            {/* TITLE */}
            <h2 className="text-base font-semibold mb-3">Product Details</h2>

            {/* GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PRODUCT_DETAIL_FIELDS.map((item) => (
                <FormField
                  key={item.field}
                  label={item.label}
                  required={item.required}
                >
                  <SearchableSelect
                    value={current[item?.field] || ""}
                    options={item?.options}
                    onChange={(value) => update(item?.field, value)}
                  />
                </FormField>
              ))}

              {/* MANUFACTURER NAME */}
              <FormField label="Manufacturer Name" required>
                <input
                  type="text"
                  value={current?.manufacturerName || ""}
                  onChange={(e) => update("manufacturerName", e.target.value)}
                  className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                />
              </FormField>

              {/* MANUFACTURER ADDRESS */}
              <FormField label="Manufacturer Address" required>
                <input
                  type="text"
                  value={current?.manufacturerAddress || ""}
                  onChange={(e) =>
                    update("manufacturerAddress", e.target.value)
                  }
                  className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                />
              </FormField>

              {/* MANUFACTURER PINCODE */}
              <FormField label="Manufacturer Pincode" required>
                <input
                  type="text"
                  value={current?.manufacturerPincode || ""}
                  onChange={(e) =>
                    update("manufacturerPincode", e.target.value)
                  }
                  className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                />
              </FormField>

              <FormField label="Wash Care">
                <SearchableSelect
                  value={current?.washCare || ""}
                  options={[
                    "Machine Wash",
                    "Hand Wash",
                    "Dry Clean Only",
                    "Do Not Wash",
                    "Gentle Wash",
                  ]}
                  onChange={(value) => update("washCare", value)}
                />
              </FormField>
            </div>
          </div>

          <div className="mt-4 p-5">
            <h2 className="text-base font-semibold mb-3">
              Model & Product Extra Info
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Brand */}
              <FormField label="Brand Name">
                <input
                  value={current?.brandName || ""}
                  onChange={(e) => update("brandName", e.target.value)}
                  className="h-10 w-full border rounded px-3 text-sm"
                />
              </FormField>

              {/* Gender */}
              <FormField label="Gender">
                <SearchableSelect
                  value={current?.gender || ""}
                  options={["Men", "Women", "Kids", "Unisex"]}
                  onChange={(value) => update("gender", value)}
                />
              </FormField>

              {/* Garment Type */}
              <FormField label="Garment Type">
                <SearchableSelect
                  value={current?.garmentType || ""}
                  options={["Top", "Bottom", "Dress", "Co-ord", "Set"]}
                  onChange={(value) => update("garmentType", value)}
                />
              </FormField>

              {/* Fit Type */}
              <FormField label="Fit Type">
                <SearchableSelect
                  value={current?.fitType || ""}
                  options={["Slim", "Regular", "Loose", "Oversized"]}
                  onChange={(value) => update("fitType", value)}
                />
              </FormField>

              {/* Model Height */}
              <FormField label="Model Height">
                <input
                  value={current?.modelHeight || ""}
                  onChange={(e) => update("modelHeight", e.target.value)}
                  placeholder="e.g. 5'8"
                  className="h-10 w-full border rounded px-3 text-sm"
                />
              </FormField>

              {/* Model Wearing Size */}
              <FormField label="Model Wearing Size">
                <SearchableSelect
                  value={current?.modelWearingSize || ""}
                  options={SIZE_LIST}
                  onChange={(value) => update("modelWearingSize", value)}
                />
              </FormField>
            </div>
          </div>

          <div className="mt-4 p-5">
            <h2 className="text-base font-semibold mb-3">Set / Combo Info</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Product Type">
                <select
                  value={current?.isSet ? "set" : "single"}
                  onChange={(e) => update("isSet", e.target.value === "set")}
                  className="h-10 w-full border rounded px-3 text-sm"
                >
                  <option value="single">Single Product</option>
                  <option value="set">Set / Combo Product</option>
                </select>
              </FormField>

              <FormField label="Set Items Count">
                <input
                  type="number"
                  value={current?.setItemsCount ?? ""}
                  onChange={(e) =>
                    update(
                      "setItemsCount",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="h-10 w-full border rounded px-3 text-sm"
                />
              </FormField>

              <FormField label="Included Items (comma separated)">
                <input
                  value={current?.includedItems?.join(", ") || ""}
                  onChange={(e) =>
                    update(
                      "includedItems",
                      e.target.value.split(",").map((i) => i.trim()),
                    )
                  }
                  className="h-10 w-full border rounded px-3 text-sm"
                />
              </FormField>
            </div>
          </div>

          <div className="mt-4 p-5">
            <h2 className="text-base font-semibold mb-3">
              Return & Refund Policy
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Return Allowed */}
              <FormField label="Return Allowed">
                <input
                  type="checkbox"
                  checked={current?.returnAllowed || false}
                  onChange={(e) => update("returnAllowed", e.target.checked)}
                />
              </FormField>

              {/* Return Days */}
              <FormField label="Return Days">
                <input
                  type="number"
                  value={current?.returnDays ?? ""}
                  onChange={(e) =>
                    update(
                      "returnDays",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="h-10 w-full border rounded px-3 text-sm"
                  placeholder="e.g. 7"
                />
              </FormField>
              {/* Refund Allowed */}
              <FormField label="Refund Allowed">
                <input
                  type="checkbox"
                  checked={current?.refundAllowed || false}
                  onChange={(e) => update("refundAllowed", e.target.checked)}
                />
              </FormField>

              {/* Exchange Days (New Field) */}
              <FormField label="Exchange Days">
                <input
                  type="number"
                  value={current?.exchangeDays ?? ""}
                  onChange={(e) =>
                    update(
                      "exchangeDays",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="h-10 w-full border rounded px-3 text-sm"
                  placeholder="e.g. 7"
                />
              </FormField>

              {/* Exchange Allowed */}
              <FormField label="Exchange Allowed">
                <input
                  type="checkbox"
                  checked={current?.exchangeAllowed || false}
                  onChange={(e) => update("exchangeAllowed", e.target.checked)}
                />
              </FormField>
            </div>
          </div>

          {/* OTHER ATTRIBUTES */}
          <div className="mt-4 p-5">
            {/* TITLE */}
            <h2 className="text-base font-semibold mb-3">Other Attributes</h2>

            {/* GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 md:gap-x-8 gap-y-5">
              {/* Sleeve Styling */}
              <FormField label="Sleeve Styling" required>
                <SearchableSelect
                  value={current?.sleeveStyling || ""}
                  options={[
                    "Batwing",
                    "Bell",
                    "Cap",
                    "Cape",
                    "Cold Shoulder",
                    "Cuffed",
                    "Cut Out",
                    "Extended",
                    "Flared",
                    "Flutter",
                    "Kimono",
                    "One Side Sleeve",
                    "Puff",
                    "Raglan",
                    "Regular",
                    "Roll-Up",
                    "Shoulder Strap",
                    "Sleeveless",
                    "Slit",
                  ]}
                  onChange={(value) => update("sleeveStyling", value)}
                />
              </FormField>

              {/* Description */}
              <FormField label="Description">
                <textarea
                  rows={5}
                  value={current?.description || ""}
                  onChange={(e) => update("description", e.target.value)}
                  placeholder="Enter product description..."
                  className="w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  maxLength={1400}
                />
                <p className="text-xs text-gray-400 text-right mt-1">
                  {current?.description?.length || 0}/1400
                </p>
              </FormField>
            </div>
          </div>
        </div>

        {/* RIGHT IMAGE PANEL */}
        <div className="col-span-12 md:col-span-4 bg-white p-5 rounded-xl">
          <ImageUploader
            activeIndex={activeIndex}
            setLoading={setLoading}
            catalogId={catalogId}
          />
        </div>
      </div>

      {/* FIXED ACTION */}
      <BottomHeader
        catalogId={catalogId}
        products={products}
        setLoading={setLoading}
      />
      {loading && <FullScreenLoader />}
    </div>
  );
}
